import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import api from '../lib/api';
import { Pagination } from '../types/api';
import { normalizePagination } from '../api/client';
import {
  LearningProgram,
  LearningProgramDetail,
  ProgramsListResponse,
  ProgramDetailResponse,
  ProgramResponse,
  ProgramDayWithCourses,
  ProgramDayResponse,
  ProgramDayCourse,
  ProgramDayCourseResponse,
  ProgramAudience,
  ProgramAudienceWithLearners,
  AudienceResponse,
  AudiencesListResponse,
  ProgramsQueryParams,
  CreateProgramData,
  UpdateProgramData,
  BatchDayData,
  UpdateDayData,
  AddDayCourseData,
  ReorderItem,
  CreateAudienceData,
  UpdateAudienceData,
  ProgramStatus,
} from '../types/program';

// ─── Programs ────────────────────────────────────────────────────────────

export interface ProgramStatusCounts {
  draft: number;
  active: number;
  disabled: number;
  archived: number;
}

const EMPTY_PROGRAM_STATUS_COUNTS: ProgramStatusCounts = {
  draft: 0,
  active: 0,
  disabled: 0,
  archived: 0,
};

export const fetchPrograms = async (
  params: ProgramsQueryParams = {}
): Promise<{
  programs: LearningProgram[];
  pagination: Pagination;
  statusCounts: ProgramStatusCounts;
}> => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.per_page) queryParams.append('per_page', params.per_page.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.status) queryParams.append('status', params.status);

  const response = await api.get<ProgramsListResponse>(
    `/programs?${queryParams.toString()}`
  );
  return {
    programs: response.data.data.programs ?? [],
    pagination: normalizePagination(response.data.data.pagination),
    statusCounts:
      (response.data.data as { status_counts?: ProgramStatusCounts }).status_counts ??
      EMPTY_PROGRAM_STATUS_COUNTS,
  };
};

export const usePrograms = (
  params: ProgramsQueryParams = {}
): UseQueryResult<
  { programs: LearningProgram[]; pagination: Pagination; statusCounts: ProgramStatusCounts },
  Error
> => {
  return useQuery({
    queryKey: ['programs', params],
    queryFn: () => fetchPrograms(params),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
    placeholderData: (prev) => prev,
  });
};

export const fetchProgram = async (programId: string): Promise<LearningProgramDetail> => {
  const response = await api.get<ProgramDetailResponse>(`/programs/${programId}`);
  return response.data.data;
};

export const useProgram = (
  programId: string | undefined
): UseQueryResult<LearningProgramDetail, Error> => {
  return useQuery({
    queryKey: ['programs', programId],
    queryFn: () => fetchProgram(programId!),
    enabled: programId !== undefined,
    staleTime: 2 * 60 * 1000,
  });
};

export const createProgram = async (data: CreateProgramData): Promise<LearningProgramDetail> => {
  const response = await api.post<ProgramDetailResponse>('/programs', data);
  return response.data.data;
};

export const useCreateProgram = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createProgram,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programs'] });
    },
  });
};

export const updateProgram = async ({
  programId,
  data,
}: {
  programId: string;
  data: UpdateProgramData;
}): Promise<LearningProgramDetail> => {
  const response = await api.put<ProgramDetailResponse>(`/programs/${programId}`, data);
  return response.data.data;
};

export const useUpdateProgram = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateProgram,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['programs'] });
      queryClient.invalidateQueries({ queryKey: ['programs', variables.programId] });
    },
  });
};

export const deleteProgram = async (programId: string): Promise<void> => {
  await api.delete(`/programs/${programId}`);
};

export const useDeleteProgram = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteProgram,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programs'] });
    },
  });
};

export const updateProgramStatus = async ({
  programId,
  status,
}: {
  programId: string;
  status: ProgramStatus;
}): Promise<LearningProgram> => {
  const response = await api.put<ProgramResponse>(`/programs/${programId}/status`, { status });
  return response.data.data;
};

export const useUpdateProgramStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateProgramStatus,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['programs'] });
      queryClient.invalidateQueries({ queryKey: ['programs', variables.programId] });
    },
  });
};

// ─── Program Days ────────────────────────────────────────────────────────

export const batchUpdateDays = async ({
  programId,
  days,
}: {
  programId: string;
  days: BatchDayData[];
}): Promise<LearningProgramDetail> => {
  const response = await api.put<ProgramDetailResponse>(
    `/programs/${programId}/days/batch`,
    { days }
  );
  return response.data.data;
};

export const useBatchUpdateDays = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: batchUpdateDays,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['programs', variables.programId] });
    },
  });
};

export const updateProgramDay = async ({
  programId,
  dayId,
  data,
}: {
  programId: string;
  dayId: string;
  data: UpdateDayData;
}): Promise<ProgramDayWithCourses> => {
  const response = await api.put<ProgramDayResponse>(
    `/programs/${programId}/days/${dayId}`,
    data
  );
  return response.data.data;
};

export const useUpdateProgramDay = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateProgramDay,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['programs', variables.programId] });
    },
  });
};

// ─── Day Courses ─────────────────────────────────────────────────────────

export const addDayCourse = async ({
  programId,
  dayId,
  data,
}: {
  programId: string;
  dayId: string;
  data: AddDayCourseData;
}): Promise<ProgramDayCourse> => {
  const response = await api.post<ProgramDayCourseResponse>(
    `/programs/${programId}/days/${dayId}/courses`,
    data
  );
  return response.data.data;
};

export const useAddDayCourse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addDayCourse,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['programs', variables.programId] });
    },
  });
};

export const removeDayCourse = async ({
  programId,
  dayId,
  dayCourseId,
}: {
  programId: string;
  dayId: string;
  dayCourseId: string;
}): Promise<void> => {
  await api.delete(`/programs/${programId}/days/${dayId}/courses/${dayCourseId}`);
};

export const useRemoveDayCourse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeDayCourse,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['programs', variables.programId] });
    },
  });
};

export const reorderDayCourses = async ({
  programId,
  dayId,
  items,
}: {
  programId: string;
  dayId: string;
  items: ReorderItem[];
}): Promise<ProgramDayWithCourses> => {
  const response = await api.put<ProgramDayResponse>(
    `/programs/${programId}/days/${dayId}/courses/reorder`,
    { items }
  );
  return response.data.data;
};

export const useReorderDayCourses = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reorderDayCourses,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['programs', variables.programId] });
    },
  });
};

// ─── Audiences ───────────────────────────────────────────────────────────

export const fetchAudiences = async (
  programId: string
): Promise<ProgramAudience[]> => {
  const response = await api.get<AudiencesListResponse>(
    `/programs/${programId}/audiences`
  );
  return response.data.data.audiences ?? [];
};

export const useAudiences = (
  programId: string | undefined
): UseQueryResult<ProgramAudience[], Error> => {
  return useQuery({
    queryKey: ['programs', programId, 'audiences'],
    queryFn: () => fetchAudiences(programId!),
    enabled: programId !== undefined,
    staleTime: 2 * 60 * 1000,
  });
};

export const fetchAudience = async ({
  programId,
  audienceId,
}: {
  programId: string;
  audienceId: string;
}): Promise<ProgramAudienceWithLearners> => {
  const response = await api.get<AudienceResponse>(
    `/programs/${programId}/audiences/${audienceId}`
  );
  return response.data.data;
};

export const useAudience = (
  programId: string | undefined,
  audienceId: string | undefined
): UseQueryResult<ProgramAudienceWithLearners, Error> => {
  return useQuery({
    queryKey: ['programs', programId, 'audiences', audienceId],
    queryFn: () => fetchAudience({ programId: programId!, audienceId: audienceId! }),
    enabled: programId !== undefined && audienceId !== undefined,
    staleTime: 2 * 60 * 1000,
  });
};

export const createAudience = async ({
  programId,
  data,
}: {
  programId: string;
  data: CreateAudienceData;
}): Promise<ProgramAudienceWithLearners> => {
  const response = await api.post<AudienceResponse>(
    `/programs/${programId}/audiences`,
    data
  );
  return response.data.data;
};

export const useCreateAudience = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAudience,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['programs', variables.programId] });
    },
  });
};

export const updateAudience = async ({
  programId,
  audienceId,
  data,
}: {
  programId: string;
  audienceId: string;
  data: UpdateAudienceData;
}): Promise<ProgramAudience> => {
  const response = await api.put<{ status: 'success'; data: ProgramAudience }>(
    `/programs/${programId}/audiences/${audienceId}`,
    data
  );
  return response.data.data;
};

export const useUpdateAudience = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAudience,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['programs', variables.programId] });
    },
  });
};

export const deleteAudience = async ({
  programId,
  audienceId,
}: {
  programId: string;
  audienceId: string;
}): Promise<void> => {
  await api.delete(`/programs/${programId}/audiences/${audienceId}`);
};

export const useDeleteAudience = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAudience,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['programs', variables.programId] });
    },
  });
};

/**
 * Fire the invitation for an audience's pending members.
 *
 * The endpoint existed but nothing in the app called it: a program's audiences
 * could be configured but never sent, while courses had "Save & send" on the
 * same wizard (Aleix, 2026-09-11 — courses and programs must not drift apart).
 * Sending also activates a draft program, exactly as the course flow does.
 */
export const sendAudience = async ({
  programId,
  audienceId,
}: {
  programId: string;
  audienceId: string;
}): Promise<void> => {
  await api.post(`/programs/${programId}/audiences/${audienceId}/send`, {});
};

export const useSendAudience = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sendAudience,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['programs', variables.programId] });
    },
  });
};

export const addAudienceLearners = async ({
  programId,
  audienceId,
  learnerIds,
}: {
  programId: string;
  audienceId: string;
  learnerIds: string[];
}): Promise<ProgramAudienceWithLearners> => {
  const response = await api.post<AudienceResponse>(
    `/programs/${programId}/audiences/${audienceId}/learners`,
    { learner_ids: learnerIds }
  );
  return response.data.data;
};

export const useAddAudienceLearners = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addAudienceLearners,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['programs', variables.programId] });
    },
  });
};

export const removeAudienceLearners = async ({
  programId,
  audienceId,
  learnerIds,
}: {
  programId: string;
  audienceId: string;
  learnerIds: string[];
}): Promise<ProgramAudienceWithLearners> => {
  const response = await api.delete<AudienceResponse>(
    `/programs/${programId}/audiences/${audienceId}/learners`,
    { data: { learner_ids: learnerIds } }
  );
  return response.data.data;
};

export const useRemoveAudienceLearners = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeAudienceLearners,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['programs', variables.programId] });
    },
  });
};
